
import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { X, Plus } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface HashtagSuggestion {
  hashtag: string;
  count: number;
}

interface HashtagInputProps {
  hashtags: string[];
  onHashtagsChange: (hashtags: string[]) => void;
  maxHashtags?: number;
  placeholder?: string;
  disabled?: boolean;
}

export const HashtagInput = ({ 
  hashtags, 
  onHashtagsChange, 
  maxHashtags = 10,
  placeholder = "Add interests/hashtags...",
  disabled = false
}: HashtagInputProps) => {
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState<HashtagSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (inputValue.length < 2 || disabled) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      setLoading(true);
      try {
        const response = await fetch(
          `https://miygybeltetsajkjsrwy.supabase.co/functions/v1/suggest-hashtag?q=${encodeURIComponent(inputValue)}`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im1peWd5YmVsdGV0c2Fqa2pzcnd5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDgxMjAwNTQsImV4cCI6MjA2MzY5NjA1NH0.FM9A1y5eklgE6vofImQCqyp4NSh4iNiM1cJre3SJ20k`,
              'Content-Type': 'application/json',
            },
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          setSuggestions(data.suggestions || []);
          setShowSuggestions(true);
        }
      } catch (error) {
        console.error('Error fetching hashtag suggestions:', error);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounceTimer);
  }, [inputValue, disabled]);

  const addHashtag = (hashtag: string) => {
    if (disabled) return;
    
    const cleanHashtag = hashtag.toLowerCase().trim();
    
    if (!cleanHashtag) return;
    
    if (hashtags.includes(cleanHashtag)) {
      toast({
        title: "Duplicate hashtag",
        description: "This interest is already added",
        variant: "destructive",
      });
      return;
    }

    if (hashtags.length >= maxHashtags) {
      toast({
        title: "Maximum reached",
        description: `You can only add up to ${maxHashtags} interests`,
        variant: "destructive",
      });
      return;
    }

    onHashtagsChange([...hashtags, cleanHashtag]);
    setInputValue("");
    setShowSuggestions(false);
  };

  const removeHashtag = (indexToRemove: number) => {
    if (disabled) return;
    onHashtagsChange(hashtags.filter((_, index) => index !== indexToRemove));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addHashtag(inputValue);
    }
  };

  return (
    <div className="space-y-3">
      <div className="relative">
        <Input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          className="pr-10"
          disabled={disabled}
        />
        <Button
          type="button"
          size="sm"
          className="absolute right-1 top-1 h-8 w-8"
          onClick={() => addHashtag(inputValue)}
          disabled={disabled || !inputValue.trim() || hashtags.length >= maxHashtags}
        >
          <Plus className="h-4 w-4" />
        </Button>
        
        {showSuggestions && suggestions.length > 0 && !disabled && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-40 overflow-y-auto">
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                className="w-full px-3 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                onClick={() => addHashtag(suggestion.hashtag)}
                disabled={disabled}
              >
                <span className="font-medium">#{suggestion.hashtag}</span>
                <span className="text-sm text-gray-500 ml-2">({suggestion.count} users)</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {hashtags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {hashtags.map((hashtag, index) => (
            <Badge key={index} variant="secondary" className="flex items-center gap-1">
              #{hashtag}
              <button
                type="button"
                onClick={() => removeHashtag(index)}
                className="ml-1 hover:bg-gray-300 rounded-full p-0.5"
                disabled={disabled}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      
      <p className="text-sm text-gray-500">
        {hashtags.length}/{maxHashtags} interests added
      </p>
    </div>
  );
};
